import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import MediaCard from "@/components/base/MediaCard";
import EmptyState from "@/components/base/EmptyState";
import { searchAll } from "@/lib/media";
import type { MediaItem, NewsItem } from "@/types/media";

type ResultTab = "all" | "movie" | "tvshow" | "show" | "news";

const tabs: { key: ResultTab; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tvshow", label: "电视剧" },
  { key: "show", label: "综艺" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(urlKeyword);
  const [tab, setTab] = useState<ResultTab>("all");

  useEffect(() => {
    setInput(urlKeyword);
    setTab("all");
  }, [urlKeyword]);

  const result = useMemo(() => searchAll(urlKeyword), [urlKeyword]);
  const totalCount = result.media.length + result.news.length;

  const showMedia =
    tab === "all" || tab === "movie" || tab === "tvshow" || tab === "show";
  const showNews = tab === "all" || tab === "news";

  const mediaList: MediaItem[] = result.media.filter(
    (item) => tab === "all" || item.type === tab,
  );
  const newsList: NewsItem[] = showNews ? result.news : [];

  usePageMeta({
    title: urlKeyword ? `“${urlKeyword}” 搜索结果 - 91蜜桃` : "搜索 - 91蜜桃",
    description: urlKeyword
      ? `91蜜桃关于“${urlKeyword}”的搜索结果，包含相关影视作品与资讯内容。`
      : "在 91蜜桃搜索电影、电视剧、综艺与影视资讯。",
    keywords: `91蜜桃搜索,${urlKeyword},影视搜索,影视资源`,
  });

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  const visibleCount =
    (showMedia ? mediaList.length : 0) + (showNews ? newsList.length : 0);

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            搜索影视与资讯
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入作品名称、类型、演员或资讯关键词，即可一站检索。
          </p>
          <div className="mt-5 flex max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-50 py-1.5 pl-4 pr-1.5 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit(input);
              }}
              placeholder="例如：科幻、流浪地球、悬疑、综艺"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => submit(input)}
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        {urlKeyword ? (
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-500">
                关键词
                <span className="mx-1 font-bold text-primary-600">“{urlKeyword}”</span>
                共找到 <span className="font-bold text-foreground-900">{totalCount}</span> 条结果
              </p>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1 sm:w-fit">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {visibleCount > 0 ? (
              <div className="mt-8 space-y-12">
                {showMedia && mediaList.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-film-line text-primary-600" />
                      影视作品
                      <span className="text-sm font-normal text-foreground-400">
                        {mediaList.length} 部
                      </span>
                    </h2>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
                      {mediaList.map((item, index) => (
                        <MediaCard key={item.id} item={item} index={index} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews && newsList.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-newspaper-line text-primary-600" />
                      相关资讯
                      <span className="text-sm font-normal text-foreground-400">
                        {newsList.length} 条
                      </span>
                    </h2>
                    <div className="space-y-3">
                      {newsList.map((item) => (
                        <Link
                          key={item.id}
                          to={`/news/${item.id}`}
                          className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:bg-background-100"
                        >
                          <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                            <img
                              src={item.cover}
                              alt={item.title}
                              title={`${item.title} - 91蜜桃影视资讯`}
                              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="min-w-0">
                            <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                              {item.title}
                            </h3>
                            <p className="mt-1.5 line-clamp-2 text-xs text-foreground-500 md:text-sm">
                              {item.summary}
                            </p>
                            <p className="mt-1.5 flex items-center gap-1 text-xs text-foreground-400">
                              <i className="ri-calendar-line" />
                              {item.date}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="mt-8">
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与“${urlKeyword}”相关的内容`}
                  desc="换个关键词试试，比如作品名称、类型或演员名字。"
                  actionLabel="查看全部电影"
                  onAction={() => setSearchParams({ q: "科幻" })}
                  backHome
                />
              </div>
            )}
          </>
        ) : (
          <div className="mt-4">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
              大家都在搜
            </h2>
            <div className="flex flex-wrap gap-2">
              {["科幻", "悬疑", "古装", "综艺", "爱情", "刑侦", "音乐", "高分剧集"].map(
                (word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => submit(word)}
                    className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {word}
                  </button>
                ),
              )}
            </div>
            <div className="mt-8">
              <EmptyState
                icon="ri-search-line"
                title="输入关键词开始搜索"
                desc="支持搜索影视作品名称、类型、演员与资讯内容。"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}